<template>
  <div class="padding-xs wrapper" :class="{ defaultWrapper: primary && isSetDefaultCardEnabled }">
    <div v-if="primary && isSetDefaultCardEnabled" class="heavy defaultCard">Default</div>
    <template v-if="card.creditSummary">
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
      <div
        :card="card"
        class="grid-x card-details"
        :class="{ 'align-center-middle': isSetDefaultCardEnabled }"
        tabindex="0"
        @click.prevent="selectedCard(card)"
        @keyup.enter.prevent="selectedCard(card)"
      >
        <div class="cell small-8">
          <div v-if="!isSetDefaultCardEnabled" class="icon icon-wallet-o-bk-large wallet"></div>
          <p v-if="primary && !isSetDefaultCardEnabled" class="default-card margin-left-xxs small">
            {{ WALLET.defaultLabel }}
          </p>
          <p
            id="card-holder-name"
            class="name medium"
            :class="{ 'margin-bottom-xxs': isSetDefaultCardEnabled, 'margin-top-xxs': !isSetDefaultCardEnabled }"
            data-fs-mask
          >
            {{ cardholderName }}
          </p>
          <p class="digits gray-1-color small" data-fs-mask>Ending in {{ last4digits }}</p>
          <p
            id="expiryDetails"
            class="expiry-date mgn-btm small"
            :class="isCardExpired(expiryMonth, expiryYear) ? 'expired-card-color' : 'gray-1-color'"
          >
            <span v-if="hasExpiry" data-fs-mask>Exp: {{ expiryMonth }}/{{ expiryYear }}</span>
          </p>
        </div>

        <div class="cell small-4 logo-container">
          <picture>
            <source :srcset="cardImgAvif" type="image/avif" />
            <source :srcset="cardImgWebp" type="image/webp" />
            <img :src="cardImg" class="card-logo" :alt="cardTypeDesc" />
          </picture>
        </div>
      </div>
      <div class="grid-x" :class="{ 'align-middle': isSetDefaultCardEnabled }">
        <div :class="{ 'small-6': isSetDefaultCardEnabled, 'small-12': !isSetDefaultCardEnabled }" class="card-links">
          <Button :card="card" :variant="'link'" class="link-sm btm-link" @click.prevent="selectedCard(card)">
            {{ WALLET.editLabel }}
          </Button>
        </div>
        <div v-if="isSetAsDefaultCard && isSetDefaultCardEnabled" class="small-6 defaultBtn">
          <Button :card="card" class="pill-button blackText" @click.prevent="setCardAsDefault(card)">
            {{ WALLET.setAsDefaultLabel }}
          </Button>
        </div>
      </div>
    </template>
  </div>
</template>
<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { fireTag } from '@vue-util/tagManager';
import Button from '@atomic-ui/button/Button';
import useSlideout from '@/composables/useSlideout';
import { useWalletStore } from '@/stores/useWalletStore';
import { useDashboardStore } from '@/stores/useDashboardStore';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';
import { WALLET } from 'config';

const dashboardStore = useDashboardStore();
const envPropsStore = useEnvPropsStore();
const { nuxtAppCDNUrl } = storeToRefs(envPropsStore);
const { killswitches } = storeToRefs(dashboardStore);
const isSetDefaultCardEnabled = computed(() => killswitches?.value?.defaultCardEnabled);

const props = defineProps({
  card: {
    type: Object,
    default: () => ({}),
  },
  persistentLoginObj: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['callingStepUpOverlay']);

const walletStore = useWalletStore();
const { showSlideout } = useSlideout();

const cardSummary = computed(() => props?.card?.creditSummary?.[0] || {});
const cardType = computed(() => cardSummary.value?.cardType);
const last4digits = computed(() => cardSummary.value?.last4digits);
const expiryMonth = computed(() => cardSummary.value?.expiryMonth);
const expiryYear = computed(() => cardSummary.value?.expiryYear);
const billingAddress = computed(() => cardSummary.value?.billingAddress || {});
const cardTypeDesc = computed(() => cardSummary.value?.cardTypeDesc);
const primary = computed(() => cardSummary.value?.primary);
const cardImg = computed(() => {
  const imageName = WALLET.cardImages[cardType.value]?.image;
  return imageName ? `${nuxtAppCDNUrl.value}/img/${imageName}` : '';
});
const cardImgWebp = computed(() => {
  return cardImg.value ? cardImg.value.replace(/\.png$/, '.webp').replace(/\.jpg$/, '.webp') : '';
});
const cardImgAvif = computed(() => {
  return cardImg.value ? cardImg.value.replace(/\.png$/, '.avif').replace(/\.jpg$/, '.avif') : '';
});

const cardholderName = computed(() => {
  const firstName = billingAddress.value?.firstName || '';
  const lastName = billingAddress.value?.lastName || '';
  if (firstName && lastName) {
    const UpperFirst = firstName.charAt(0).toUpperCase() + firstName.slice(1);
    const UpperLast = lastName.charAt(0).toUpperCase() + lastName.slice(1);
    return `${UpperFirst} ${UpperLast}`;
  }
  return '';
});

const hasExpiry = computed(() => {
  if (['Y', 'U', 'F'].includes(cardType.value)) {
    return false;
  }
  return expiryMonth.value && expiryYear.value;
});

const isCardExpired = (expiryMonth, expiryYear) => {
  if (!expiryMonth || !expiryYear) return false;

  const now = new Date();
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth() + 1;
  const year = Number(expiryYear);
  return year < currentYear || (year === currentYear && Number(expiryMonth) < currentMonth);
};

const isSetAsDefaultCard = computed(() => {
  const isAllowedType = ['B', 'F', 'Y', 'U'].includes(cardType.value);
  const isNotPrimary = !primary.value;
  // For card type 'Y' and F, skip the expiry check
  if (['F', 'Y'].includes(cardType.value)) {
    return isAllowedType && isNotPrimary;
  }
  // For other types, check if card is not expired
  return isAllowedType && isNotPrimary && !isCardExpired(expiryMonth.value, expiryYear.value);
});

const previousDefaultUserCardGuid = computed(
  () => walletStore.getWalletCards?.find((item) => item.creditSummary?.[0]?.primary)?.creditSummary[0]?.userCardGuid,
);

const selectedCard = async (card, callbackFunction) => {
  const component = import('./PaymentCardsSlideout');
  if (props.persistentLoginObj.persistentLoginEnabled && walletStore.extendToken && !callbackFunction) {
    emit('callingStepUpOverlay', selectedCard.bind(null, card));
    fireTag('view', {
      ...WALLET.tag.editCard,
      page_type: 'step up login',
      page_name: 'home wallet edit',
    });
    return;
  }
  if (callbackFunction) {
    walletStore.setExtendToken(false);
  }
  showSlideout('wallet-slideout', WALLET.heading, component, true, {
    card,
    imgSrc: cardImg.value,
    fullName: cardholderName.value,
  });
  fireTag('link', {
    ...WALLET.tag.editCard,
  });
};

const setCardAsDefault = async (card, callbackFunction) => {
  if (props.persistentLoginObj.persistentLoginEnabled && walletStore.extendToken && !callbackFunction) {
    emit('callingStepUpOverlay', setCardAsDefault.bind(null, card));
    fireTag('view', {
      event_name: 'set as default',
      link_name: 'set as default',
      page_type: 'step up login',
      page_name: 'home wallet set as default',
    });
    return;
  }
  if (callbackFunction) {
    walletStore.setExtendToken(false);
  }
  try {
    const defaultUserCardGuid = card?.creditSummary[0]?.userCardGuid;
    await walletStore.setAsDefaultCard(defaultUserCardGuid, previousDefaultUserCardGuid.value);
    await dashboardStore.fetchRewardsSummary().catch((err) => {
      console.error('Failed to fetch rewards summary', err);
    });
  } catch (error) {
    console.error('Failed to set default card', error?.data?.defaultCardResponse);
  }
};
</script>

<style lang="scss" scoped>
.card-details {
  height: rem-calc(112);
}

.wrapper {
  border: $gray-3-border;
  box-sizing: border-box;
  height: 100%;
}

.defaultWrapper {
  border: $black-border;
  position: relative;
  box-sizing: border-box;
  display: block;
  height: 100%;
}

.defaultCard {
  height: 26px;
  background-color: $black;
  width: 75px;
  color: $white;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
}

.blackText {
  color: $black;
}

.defaultBtn {
  display: flex;
  justify-content: flex-end;
}

.expired-card-color {
  color: $error-color;
}
</style>

<style lang="scss" brand="mcom" scoped>
@import '@csgIcons/_wallet.scss';

.wrapper {
  border-radius: 5px;
}

.defaultCard {
  height: 26px;
  background-color: $black;
  width: 75px;
  border-radius: 5px 0 5px 0;
  color: $white;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
}

.defaultWrapper {
  border: $black-border;
  border-radius: 5px;
  position: relative;
  box-sizing: border-box;
  display: block;
  height: 100%;
}
</style>

<style lang="scss" brand="bcom" scoped>
@import '@csgIcons/_bwallet.scss';

.wallet {
  @extend .bwallet;
}
</style>
